import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { Animated, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z as zod } from 'zod';
import { useAuthStore } from '../../../common/auth/store';
import { LoginLogic, LoginFormValues } from '../types';

/**
 * Hook that encapsulates all the business logic for the Login screen.
 * Uses react-hook-form and zod for robust form management and validation.
 *
 * @returns Object containing state and handlers for the login flow
 */
export const useLoginLogic = (): LoginLogic => {
  const { t } = useTranslation();
  const { login } = useAuthStore();

  const [apiError, setApiError] = useState('');
  const [showLoadingDelayed, setShowLoadingDelayed] = useState(false);

  // Define schema locally to use localized error messages
  const loginSchema = useMemo(() => zod.object({
    username: zod.string().min(1, t('login.errors.missing-credentials')),
    password: zod.string().min(1, t('login.errors.missing-credentials')),
    rememberMe: zod.boolean().optional(),
  }), [t]);

  // Initialize react-hook-form
  const formMethods = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    // We use manual validation trigger to match the test expectations of immediate feedback on submit
    // and manual clearing on input change.
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false,
    },
  });

  const { formState: { isSubmitting }, clearErrors } = formMethods;

  // Animation state
  const fadeAnim = useMemo(() => new Animated.Value(0), []);
  const slideAnim = useMemo(() => new Animated.Value(20), []);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();
  }, [fadeAnim, slideAnim]);

  const clearApiError = useCallback(() => {
    setApiError('');
    clearErrors(); // Also clear hook-form errors to match original behavior
  }, [clearErrors]);

  // Sync the delayed loading state with isSubmitting
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isSubmitting) {
      timer = setTimeout(() => setShowLoadingDelayed(true), 500);
    } else {
      setShowLoadingDelayed(false);
    }
    return () => clearTimeout(timer);
  }, [isSubmitting]);

  /**
   * Primary login handler. Called by react-hook-form's handleSubmit.
   */
  const handleLogin = useCallback(
    async (data: LoginFormValues) => {
      setApiError('');

      try {
        await login({
          username: data.username,
          password: data.password,
          rememberMe: !!data.rememberMe,
        });
      } catch (error: unknown) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorResponse = (error as any).response;
        if (!errorResponse) {
          setApiError(t('login.errors.server-error'));
        } else {
          const errorCode = errorResponse.data?.error?.code;
          switch (errorCode) {
            case 'AUTH_DUPLICATE_EMAIL':
              setApiError(t('login.errors.duplicate-email'));
              break;
            case 'AUTH_INVALID_CREDENTIALS':
              setApiError(t('login.errors.invalid-credentials'));
              break;
            case 'AUTH_TRIAL_EXPIRED':
              setApiError(t('login.errors.trial-expired'));
              break;
            case 'AUTH_USER_INACTIVE':
              setApiError(t('login.errors.access-denied'));
              break;
            default:
              setApiError(t('login.errors.server-error'));
          }
        }
      }
    },
    [login, t],
  );

  return {
    formMethods,
    apiError,
    clearApiError,
    isAuthenticating: isSubmitting,
    showLoading: showLoadingDelayed,
    handleLogin,
    fadeAnim,
    slideAnim,
  };
};
